import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { isEqual } from 'lodash';
import { Store } from '@ngrx/store';
import { updateVariables } from '../../../package-designer/store/variables.actions';
import { setDidUserSaveVariablesFlag } from '../../../package-designer/store/flags.actions';
import { AppState } from '../../../store';
import { Variables } from '../../../package-designer/package.models';
import { VariablesService } from '../../../jobs/services/variables.service';
import { closePackageVariablesModal } from '../../../package-designer/store/package-designer.actions';
import { DynamicConnectionService } from '../../../package-designer/services/dynamic-connection.service';

const regexpFormat = /^[a-zA-Z_]+[\w]*$/;

function isKeyFormatValid(alias) {
  return regexpFormat.test(alias);
}

function isValueValid(value) {
  return undefined !== value && value !== null && value !== '';
}

@Component({
  selector: 'package-variables-editor',
  template: `
    <div class="package-variables-editor variables-editor" [ngClass]="{ disabled: isDisabled }">
      <div class="package-variables-editor-body">
        <div class="package-variables-editor-tabs-box">
          <div class="package-variables-editor-tabs">
            <div [ngClass]="{ active: isUserVariables }" (click)="isUserVariables = true">
              <span>{{ 'package-variables-editor.tabs.user' | translate }}</span>
            </div>
            <div [ngClass]="{ active: !isUserVariables }" (click)="isUserVariables = false">
              <span>{{ 'package-variables-editor.tabs.system' | translate }}</span>
            </div>
          </div>
          <div class="package-variables-editor-content">
            <div *ngIf="isUserVariables" [ngClass]="{ active: isUserVariables }">
              <variables-editor
                class="user-variables"
                [items]="userVariables"
                type="user_variables"
                [hideAdd]="!editable || hideAdd"
                [emptyMessage]="'variables-editor.empty-message.user-variables'"
                [disableSort]="!editable"
                [disableKey]="disableKey"
                [readonlyValue]="!editable"
                [ngClass]="{ editable: editable }"
                [hideRemove]="hideRemove"
                [showUndo]="usePackageDefault"
                [usePackageDefault]="usePackageDefault"
                (variablesChange)="onChange($event, 'userVariables')"
              ></variables-editor>
            </div>
            <div *ngIf="!isUserVariables" [ngClass]="{ active: !isUserVariables }">
              <variables-editor
                class="system-variables"
                [items]="systemVariables"
                type="system_variables"
                [readonlyValue]="!editable"
                [hideRemove]="hideRemove"
                [hideAdd]="true"
                [showUndo]="editable"
                [ngClass]="{ editable: editable }"
                [usePackageDefault]="usePackageDefault"
                [disableKey]="disableKey"
                (variablesChange)="onChange($event, 'systemVariables')"
              ></variables-editor>
            </div>
          </div>
        </div>
      </div>
      <div class="package-variables-editor-footer" *ngIf="editable && !hideFooter">
        <div class="modal-footer">
          <div class="modal-title-container">
            <h3 class="modal-title">{{ 'package-variables-editor.title' | translate }}</h3>
          </div>
          <button
            type="button"
            id="connection-button-cancel"
            class="btn btn-lg btn-default pull-right"
            (click)="cancel()"
          >
            {{ 'package-variables-editor.buttons.cancel' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-lg pull-right modal-btn-save"
            [disabled]="!valid || !changed"
            (click)="save()"
            [ngClass]="{ 'btn-success': changed, 'btn-default': !changed }"
          >
            {{ 'package-variables-editor.buttons.save' | translate }}
          </button>
        </div>
      </div>
    </div>
  `,
})
export class PackageVariablesEditorComponent implements OnChanges, OnInit {
  @Input() variables: Variables;
  @Input() variablesToSet: Variables;
  @Input() valid = true;
  @Input() error = false;
  @Input() usePackageDefault = false;
  @Input() editable = true;
  @Input() hideAdd = false;
  @Input() hideFooter = false;
  @Input() hideRemove = true;
  @Input() showUndo = false;
  @Input() disableKey = false;
  @Input() isDisabled = false;
  @Output() variablesChange = new EventEmitter();

  userVariables = [];
  systemVariables = [];
  changed = false;
  default: Variables = {};
  exitingKeys = {};
  isUserVariables = true;

  constructor(
    private variablesService: VariablesService,
    private store: Store<AppState>,
    private changeDetector: ChangeDetectorRef,
    private dynamicConnectionService: DynamicConnectionService,
  ) {}

  ngOnInit() {
    this.default = { ...this.variables };
  }

  onChange(variables, key) {
    this[key] = variables;

    this.combine(() => {
      this.variablesChange.emit(this.variables);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.variables && changes.variables.currentValue) {
      this.separateVariables();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  identify(index, item) {
    return item.key;
  }

  separateVariables() {
    this.variablesService.separateVariables(this.variables, this.variablesToSet, this.usePackageDefault, (vars) => {
      this.userVariables = vars.userVariables;
      this.systemVariables = vars.systemVariables;
      this.combine(() => {});
      this.changeDetector.markForCheck();
    });
  }

  combine(callback: () => void) {
    this.variablesService.combineVariables(this.userVariables, this.systemVariables, (variables) => {
      this.variables = variables;
      this.changed = !isEqual(variables, this.default);
      this.validate();
      callback();
    });
  }

  isKeyValid(alias) {
    let isFormatValid = isKeyFormatValid(alias);
    if (this.exitingKeys[alias]) {
      isFormatValid = false;
    } else {
      this.exitingKeys[alias] = true;
    }
    return isFormatValid && alias.length !== 0;
  }

  validateVariables(variable) {
    // eslint-disable-next-line no-param-reassign
    variable.valueInvalid = !isValueValid(variable.value);
    // eslint-disable-next-line no-param-reassign
    variable.keyInvalid = !this.isKeyValid(variable.key);
    if (variable.valueInvalid || variable.keyInvalid) {
      this.valid = false;
      this.error = !this.valid;
    }
  }

  validate() {
    this.exitingKeys = {};
    this.valid = true;
    this.error = !this.valid;
    if (this.isUserVariables && this.editable) this.userVariables.forEach(this.validateVariables.bind(this));
    if (!this.isUserVariables && this.editable) this.systemVariables.forEach(this.validateVariables.bind(this));
  }

  cancel() {
    this.store.dispatch(closePackageVariablesModal());
  }

  save() {
    let variables = { ...this.variables };
    if (this.dynamicConnectionService.dynamicConnectionVariables) {
      variables = {
        ...variables,
        ...this.dynamicConnectionService.dynamicConnectionVariables,
      };
    }
    this.dynamicConnectionService.dynamicConnectionVariables = null;

    this.store.dispatch(updateVariables({ variables }));
    this.store.dispatch(setDidUserSaveVariablesFlag({ value: true }));
    this.store.dispatch(closePackageVariablesModal());
  }

  setVariablesToSet(variables) {
    variables.forEach((variable) => {
      if (this.variablesToSet && this.variablesToSet[variable.key]) {
        // eslint-disable-next-line no-param-reassign
        variable.value = this.variablesToSet[variable.key];
      }
    });
  }
}
